import { UUID } from "uuidv7"

import type { PropertyId } from "~/classes/entity"
import type { EntityApiIdBuf, EntityPropertyGroupIdBuf } from "~/classes/generated/entity"

import { ConversationIdBuf, ConversationPropertyIdBuf } from "~/classes/generated/conversation"
import { EntityIdBuf, EntityPropertyIdBuf, UuidBuf, UuidInnerBuf } from "~/classes/generated/entity"
import { FileIdBuf, FilePropertyIdBuf } from "~/classes/generated/file"
import { SearchIdBuf, SearchPropertyIdBuf } from "~/classes/generated/search"
import { SharedFolderIdBuf } from "~/classes/generated/shared-folder"
import { UserIdBuf, UserPropertyIdBuf } from "~/classes/generated/user"

export function parseUuidBuf(buf: UuidBuf) {
  const uuid = buf.key()!
  const array = new Uint8Array([
    uuid.key0(),
    uuid.key1(),
    uuid.key2(),
    uuid.key3(),
    uuid.key4(),
    uuid.key5(),
    uuid.key6(),
    uuid.key7(),
    uuid.key8(),
    uuid.key9(),
    uuid.key10(),
    uuid.key11(),
    uuid.key12(),
    uuid.key13(),
    uuid.key14(),
    uuid.key15(),
  ])
  return UUID.ofInner(array)
}
export function addUuidToBuf(fbb: DatagramBufferBuilder, uuid: string) {
  if (fbb.uuids.has(uuid))
    return fbb.uuids.get(uuid)!
  const parsed = UUID.parse(uuid)
  const inner = UuidInnerBuf.createUuidInnerBuf(fbb,
    parsed.bytes[0],
    parsed.bytes[1],
    parsed.bytes[2],
    parsed.bytes[3],
    parsed.bytes[4],
    parsed.bytes[5],
    parsed.bytes[6],
    parsed.bytes[7],
    parsed.bytes[8],
    parsed.bytes[9],
    parsed.bytes[10],
    parsed.bytes[11],
    parsed.bytes[12],
    parsed.bytes[13],
    parsed.bytes[14],
    parsed.bytes[15],
  )
  const offset = UuidBuf.createUuidBuf(fbb, inner)
  fbb.uuids.set(uuid, offset)
  return offset
}

export function parseEntityPropertyGroupId(buf: EntityPropertyGroupIdBuf) {
  return buf
}
export function createEntityPropertyGroupIdBuf(fbb: DatagramBufferBuilder, groupId: PropertyGroup) {
  return groupId
}
export function parseEntityApiId(buf: EntityApiIdBuf) {
  return buf.toString(16).padStart(8, "0")
}

export function _parseEntityId(
  buf: EntityIdBuf | FileIdBuf | SharedFolderIdBuf | SearchIdBuf | UserIdBuf | ConversationIdBuf,
) {
  return `${parseEntityApiId(buf.apiId())}-${parseUuidBuf(buf.keyId()!).toString()}`
}
export const parseEntityId = (id: EntityIdBuf) => _parseEntityId(id)
export const parseFileId = (id: FileIdBuf) => _parseEntityId(id)
export const parseSharedFolderId = (id: SharedFolderIdBuf) => _parseEntityId(id)
export const parseUserId = (id: UserIdBuf) => _parseEntityId(id)
export const parseSearchId = (id: SearchIdBuf) => _parseEntityId(id)
export const parseConversationId = (id: ConversationIdBuf) => _parseEntityId(id)

export function addEntityIdToBuf(fbb: DatagramBufferBuilder, entityId: string) {
  if (fbb.entityIds.has(entityId))
    return fbb.entityIds.get(entityId)!
  const [api, key] = partition(entityId, "-")
  const apiId = Number.parseInt(api, 16)
  const keyId = addUuidToBuf(fbb, key)
  EntityIdBuf.startEntityIdBuf(fbb)
  EntityIdBuf.addApiId(fbb, apiId)
  EntityIdBuf.addKeyId(fbb, keyId)
  const offset = EntityIdBuf.endEntityIdBuf(fbb)
  fbb.entityIds.set(entityId, offset)
  return offset
}
export function addFileIdToBuf(fbb: DatagramBufferBuilder, fileId: string) {
  if (fbb.fileIds.has(fileId))
    return fbb.fileIds.get(fileId)!
  const [api, key] = partition(fileId, "-")
  const apiId = Number.parseInt(api, 16)
  const keyId = addUuidToBuf(fbb, key)
  FileIdBuf.startFileIdBuf(fbb)
  FileIdBuf.addApiId(fbb, apiId)
  FileIdBuf.addKeyId(fbb, keyId)
  const offset = FileIdBuf.endFileIdBuf(fbb)
  fbb.fileIds.set(fileId, offset)
  return offset
}
export function addUserIdToBuf(fbb: DatagramBufferBuilder, userId: string) {
  if (fbb.userIds.has(userId))
    return fbb.userIds.get(userId)!
  const [api, key] = partition(userId, "-")
  const apiId = Number.parseInt(api, 16)
  const keyId = addUuidToBuf(fbb, key)
  UserIdBuf.startUserIdBuf(fbb)
  UserIdBuf.addApiId(fbb, apiId)
  UserIdBuf.addKeyId(fbb, keyId)
  const offset = UserIdBuf.endUserIdBuf(fbb)
  fbb.userIds.set(userId, offset)
  return offset
}
export function addSharedFolderIdToBuf(fbb: DatagramBufferBuilder, sharedFolderId: string) {
  if (fbb.sharedFolderIds.has(sharedFolderId))
    return fbb.sharedFolderIds.get(sharedFolderId)!
  const [api, key] = partition(sharedFolderId, "-")
  const apiId = Number.parseInt(api, 16)
  const keyId = addUuidToBuf(fbb, key)
  SharedFolderIdBuf.startSharedFolderIdBuf(fbb)
  SharedFolderIdBuf.addApiId(fbb, apiId)
  SharedFolderIdBuf.addKeyId(fbb, keyId)
  const offset = SharedFolderIdBuf.endSharedFolderIdBuf(fbb)
  fbb.sharedFolderIds.set(sharedFolderId, offset)
  return offset
}
export function addSearchIdToBuf(fbb: DatagramBufferBuilder, searchId: string) {
  if (fbb.searchIds.has(searchId))
    return fbb.searchIds.get(searchId)!
  const [api, key] = partition(searchId, "-")
  const apiId = Number.parseInt(api, 16)
  const keyId = addUuidToBuf(fbb, key)
  SearchIdBuf.startSearchIdBuf(fbb)
  SearchIdBuf.addApiId(fbb, apiId)
  SearchIdBuf.addKeyId(fbb, keyId)
  const offset = SearchIdBuf.endSearchIdBuf(fbb)
  fbb.searchIds.set(searchId, offset)
  return offset
}
export function addConversationIdToBuf(fbb: DatagramBufferBuilder, conversationId: string) {
  if (fbb.conversationIds.has(conversationId))
    return fbb.conversationIds.get(conversationId)!
  const [api, key] = partition(conversationId, "-")
  const apiId = Number.parseInt(api, 16)
  const keyId = addUuidToBuf(fbb, key)
  ConversationIdBuf.startConversationIdBuf(fbb)
  ConversationIdBuf.addApiId(fbb, apiId)
  ConversationIdBuf.addKeyId(fbb, keyId)
  const offset = ConversationIdBuf.endConversationIdBuf(fbb)
  fbb.conversationIds.set(conversationId, offset)
  return offset
}

export function _parseEntityPropertyId(buf: EntityPropertyIdBuf | FilePropertyIdBuf | UserPropertyIdBuf | SearchPropertyIdBuf) {
  // TODO: validate as user property
  const groupStr = parseEntityPropertyGroupId(buf.group()!).toString(16).padStart(8, "0")
  const uuid = parseUuidBuf(buf.key()!)
  return `${groupStr}-${uuid.toString()}`
}
export const parseEntityPropertyId = (id: EntityPropertyIdBuf) => _parseEntityPropertyId(id)
export const parseFilePropertyId = (id: FilePropertyIdBuf) => _parseEntityPropertyId(id) as PropertyId
export const parseUserPropertyId = (id: UserPropertyIdBuf) => _parseEntityPropertyId(id) as PropertyId
export const parseSearchPropertyId = (id: SearchPropertyIdBuf) => _parseEntityPropertyId(id) as PropertyId
export const parseConversationPropertyId = (id: ConversationPropertyIdBuf) => _parseEntityPropertyId(id) as PropertyId

export function addEntityPropertyIdToBuf(fbb: DatagramBufferBuilder, propertyId: string) {
  if (fbb.entityPropertyIds.has(propertyId))
    return fbb.entityPropertyIds.get(propertyId)!
  const [groupId, keyId] = partition(propertyId, "-")
  const group = Number.parseInt(groupId, 16)
  const key = addUuidToBuf(fbb, keyId)
  EntityPropertyIdBuf.startEntityPropertyIdBuf(fbb)
  EntityPropertyIdBuf.addGroup(fbb, group)
  EntityPropertyIdBuf.addKey(fbb, key)
  const offset = EntityPropertyIdBuf.endEntityPropertyIdBuf(fbb)
  fbb.entityPropertyIds.set(propertyId, offset)
  return offset
}
export function addFilePropertyIdToBuf(fbb: DatagramBufferBuilder, propertyId: string) {
  if (fbb.filePropertyIds.has(propertyId))
    return fbb.filePropertyIds.get(propertyId)!
  const [groupId, keyId] = partition(propertyId, "-")
  const group = Number.parseInt(groupId, 16)
  const key = addUuidToBuf(fbb, keyId)
  FilePropertyIdBuf.startFilePropertyIdBuf(fbb)
  FilePropertyIdBuf.addGroup(fbb, group)
  FilePropertyIdBuf.addKey(fbb, key)
  const offset = FilePropertyIdBuf.endFilePropertyIdBuf(fbb)
  fbb.filePropertyIds.set(propertyId, offset)
  return offset
}
export function addUserPropertyIdToBuf(fbb: DatagramBufferBuilder, propertyId: string) {
  if (fbb.userPropertyIds.has(propertyId))
    return fbb.userPropertyIds.get(propertyId)!
  const [groupId, keyId] = partition(propertyId, "-")
  const group = Number.parseInt(groupId, 16)
  const key = addUuidToBuf(fbb, keyId)
  UserPropertyIdBuf.startUserPropertyIdBuf(fbb)
  UserPropertyIdBuf.addGroup(fbb, group)
  UserPropertyIdBuf.addKey(fbb, key)
  const offset = UserPropertyIdBuf.endUserPropertyIdBuf(fbb)
  fbb.userPropertyIds.set(propertyId, offset)
  return offset
}
export function addSearchPropertyIdToBuf(fbb: DatagramBufferBuilder, propertyId: string) {
  if (fbb.searchPropertyIds.has(propertyId))
    return fbb.searchPropertyIds.get(propertyId)!
  const [groupId, keyId] = partition(propertyId, "-")
  const group = Number.parseInt(groupId, 16)
  const key = addUuidToBuf(fbb, keyId)
  SearchPropertyIdBuf.startSearchPropertyIdBuf(fbb)
  SearchPropertyIdBuf.addGroup(fbb, group)
  SearchPropertyIdBuf.addKey(fbb, key)
  const offset = SearchPropertyIdBuf.endSearchPropertyIdBuf(fbb)
  fbb.searchPropertyIds.set(propertyId, offset)
  return offset
}
export function addConversationPropertyIdToBuf(fbb: DatagramBufferBuilder, propertyId: string) {
  if (fbb.conversationPropertyIds.has(propertyId))
    return fbb.conversationPropertyIds.get(propertyId)!
  const [groupId, keyId] = partition(propertyId, "-")
  const group = Number.parseInt(groupId, 16)
  const key = addUuidToBuf(fbb, keyId)
  ConversationPropertyIdBuf.startConversationPropertyIdBuf(fbb)
  ConversationPropertyIdBuf.addGroup(fbb, group)
  ConversationPropertyIdBuf.addKey(fbb, key)
  const offset = ConversationPropertyIdBuf.endConversationPropertyIdBuf(fbb)
  fbb.conversationPropertyIds.set(propertyId, offset)
  return offset
}
